// Imperative modal manager on Nuxt UI's useOverlay.
// open(Component, props) OR open(configObject) -> Promise resolving to the modal's close value.
import GenericModal from '~/components/fragments/modal/generic.vue';

// Body scroll lock via the app's `html.locked` class (app/assets/css/main.css:
// `html.locked { overflow: hidden }`, which ALSO hides the HubSpot chat widgets).
// This is the same mechanism app.vue uses for its modals. Ref-counted so that
// stacked modals only unlock the page once the last one has closed.
let lockCount = 0;
const lockScroll = () => {
    if (!import.meta.client) return;
    if (lockCount++ === 0) document.documentElement.classList.add('modal-locked');
};
const unlockScroll = () => {
    if (!import.meta.client) return;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.documentElement.classList.remove('modal-locked');
};

export const useModal = () => {
    const overlay = useOverlay();

    // A "config" is a plain data object (no Vue component markers).
    const isConfig = (x) => x && typeof x === 'object'
        && !x.render && !x.setup && !x.__name && !x.name && !x.__file;

    const open = (componentOrConfig, props = {}) => {
        const cfg = isConfig(componentOrConfig);
        const component = cfg ? GenericModal : componentOrConfig;
        const initialProps = cfg ? componentOrConfig : props;
        const instance = overlay.create(component, { props: initialProps, destroyOnClose: true });
        lockScroll();
        const promise = instance.open();
        Promise.resolve(promise).finally(() => unlockScroll());
        return promise;
    };

    const confirm = ({ headline, body = '', confirmLabel = 'Confirm', cancelLabel = 'Cancel', persistent = false } = {}) =>
        open({ headline, body, persistent, actions: [
            { label: confirmLabel, value: true, variant: 'primary' },
            { label: cancelLabel, value: false, variant: 'link' },
        ] }).then((v) => v === true);

    const error = ({ headline, body = '', dismissLabel = 'Close' } = {}) =>
        open({ headline, body, actions: [{ label: dismissLabel, value: null, variant: 'link' }] });

    // Same as open(), but hands back a handle so the CALLER can dismiss the
    // modal it opened. `open()` returns the close-value promise, which is the
    // right shape for "wait for the user's answer" but gives you no way to take
    // a modal down programmatically — needed for the Save a Seat processing
    // screen, which is persistent by design and has to be replaced by its
    // outcome once the reservation resolves. closeAll() would have worked but
    // takes any other overlay with it.
    const openHandle = (component, props = {}) => {
        const instance = overlay.create(component, { props, destroyOnClose: true });
        lockScroll();
        const promise = instance.open();
        Promise.resolve(promise).finally(() => unlockScroll());
        return { promise, close: (value) => instance.close(value) };
    };

    return { open, openHandle, confirm, error, closeAll: overlay.closeAll };
};
